import type { GenerateWorkflowDraftResponse } from './workflowTypes'

const COMMON_TOOLS = {
  safety: ['find_doc_sections', 'search_doc_chunks'],
  define: [
    'resolve_canonical',
    'list_control_loops_on_sheet',
    'trace_connections',
    'trace_cross_sheet',
  ],
  historian: ['query_historian'],
  documents: ['find_doc_sections', 'search_doc_chunks', 'query_datasheet'],
  topology: [
    'resolve_canonical',
    'trace_cross_sheet',
    'list_control_loops_on_sheet',
    'get_sheet',
    'list_board_items',
  ],
  synthesis: [],
} as const

export const canonicalTroubleshootingDraft: GenerateWorkflowDraftResponse = {
  definition: {
    id: 'troubleshooting_base',
    version: '1',
    title: 'Troubleshooting Base',
    description:
      'Safety-first troubleshooting guardrail that defines the problem, gathers evidence, separates symptoms from causes, and recommends next checks/actions.',
    family: 'troubleshooting',
    steps: [
      {
        id: 'stabilize-safety',
        title: 'Stabilize And Make Safe',
        kind: 'agentic',
        phase: 'stabilize',
        allowedTools: [...COMMON_TOOLS.safety],
        outputKey: 'safetyDefinition',
      },
      {
        id: 'define-problem',
        title: 'Define The Problem',
        kind: 'agentic',
        phase: 'define',
        allowedTools: [...COMMON_TOOLS.define],
        outputKey: 'problemDefinition',
      },
      {
        id: 'gather-historian',
        title: 'Gather And Verify Information',
        kind: 'agentic',
        phase: 'gather',
        allowedTools: [...COMMON_TOOLS.historian],
        outputKey: 'historianEvidence',
      },
      {
        id: 'gather-documents',
        title: 'Gather And Verify Information',
        kind: 'agentic',
        phase: 'gather',
        allowedTools: [...COMMON_TOOLS.documents],
        outputKey: 'evidence',
      },
      {
        id: 'gather-topology',
        title: 'Gather And Verify Information',
        kind: 'agentic',
        phase: 'gather',
        allowedTools: [...COMMON_TOOLS.topology],
        outputKey: 'topologyEvidence',
      },
      {
        id: 'separate-symptoms',
        title: 'Separate Symptoms From Causes',
        kind: 'agentic',
        phase: 'separate',
        allowedTools: [...COMMON_TOOLS.synthesis],
        outputKey: 'causeMap',
      },
      {
        id: 'generate-hypotheses',
        title: 'Generate Hypotheses',
        kind: 'agentic',
        phase: 'analyze',
        allowedTools: [...COMMON_TOOLS.synthesis],
        outputKey: 'hypotheses',
      },
      {
        id: 'assess-hypotheses',
        title: 'Test/Assess Hypotheses',
        kind: 'agentic',
        phase: 'analyze',
        allowedTools: [...COMMON_TOOLS.synthesis],
        outputKey: 'hypothesisAssessment',
      },
      {
        id: 'diagnose-root-cause',
        title: 'Diagnose/Root-Cause Status',
        kind: 'agentic',
        phase: 'analyze',
        allowedTools: [...COMMON_TOOLS.synthesis],
        outputKey: 'outcome',
      },
      {
        id: 'recommend-actions',
        title: 'Plan Actions, Monitoring, And Learning',
        kind: 'agentic',
        phase: 'recommend',
        allowedTools: [...COMMON_TOOLS.synthesis],
        outputKey: 'actions',
      },
    ],
  },
  planPreview: {
    title: 'Troubleshooting Base',
    operators: [],
    mode: 'lite',
    phases: [
      {
        id: 'stabilize-safety',
        title: 'Stabilize And Make Safe',
        phase: 'stabilize',
        mayAskClarification: true,
        likelyTools: [...COMMON_TOOLS.safety],
        completionCriteria: 'safety is present.',
      },
      {
        id: 'define-problem',
        title: 'Define The Problem',
        phase: 'define',
        mayAskClarification: true,
        likelyTools: [...COMMON_TOOLS.define],
        completionCriteria: 'problemStatement is present.',
      },
      {
        id: 'gather-historian',
        title: 'Gather And Verify Information',
        phase: 'gather',
        mayAskClarification: true,
        likelyTools: [...COMMON_TOOLS.historian],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'gather-documents',
        title: 'Gather And Verify Information',
        phase: 'gather',
        mayAskClarification: true,
        likelyTools: [...COMMON_TOOLS.documents],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'gather-topology',
        title: 'Gather And Verify Information',
        phase: 'gather',
        mayAskClarification: true,
        likelyTools: [...COMMON_TOOLS.topology],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'separate-symptoms',
        title: 'Separate Symptoms From Causes',
        phase: 'separate',
        mayAskClarification: false,
        likelyTools: [...COMMON_TOOLS.synthesis],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'generate-hypotheses',
        title: 'Generate Hypotheses',
        phase: 'analyze',
        mayAskClarification: false,
        likelyTools: [...COMMON_TOOLS.synthesis],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'assess-hypotheses',
        title: 'Test/Assess Hypotheses',
        phase: 'analyze',
        mayAskClarification: false,
        likelyTools: [...COMMON_TOOLS.synthesis],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'diagnose-root-cause',
        title: 'Diagnose/Root-Cause Status',
        phase: 'analyze',
        mayAskClarification: false,
        likelyTools: [...COMMON_TOOLS.synthesis],
        completionCriteria: 'Step output exists.',
      },
      {
        id: 'recommend-actions',
        title: 'Plan Actions, Monitoring, And Learning',
        phase: 'recommend',
        mayAskClarification: false,
        likelyTools: [...COMMON_TOOLS.synthesis],
        completionCriteria: 'Step output exists.',
      },
    ],
    budgets: {
      maxDurationMs: 3600000,
      maxAgentStepsPerWorkflowStep: 3,
    },
  },
  validation: { ok: true },
  sourceTemplateId: 'troubleshooting_base',
}

export function cloneCanonicalTroubleshootingDraft(): GenerateWorkflowDraftResponse {
  return structuredClone(canonicalTroubleshootingDraft)
}
